// ===========================================
// "{Name}'s family was already invoiced for {Term}" — after an enrolment
// ===========================================
// A child enrolled after their family was invoiced for the term — a second class, a second
// sibling, a mid-term joiner — is not on that invoice. The next term-invoice run normally bills them on a
// SEPARATE invoice ("bill what is unbilled", 2026-10-01); this says so at the moment of enrolment
// and offers to bill them now instead. The enrol routes return `alreadyInvoiced`.
//
// The family's invoices come FIRST (drafts → Edit, sent → Amend, paid / refunded → locked), and
// "Create an invoice for {name}" is one choice among them, never the default: a hand-made
// invoice may already include the child (md/review/already-invoiced.md). Nothing is created or
// changed automatically. Links never close the dialog — the host keeps the list with the browser
// history entry, so Back returns to it.

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import type { AlreadyInvoicedEnrolment } from '../../api/lessons.api';
import type { InvoiceToAmend } from '../../api/invoices.api';
import { usePermissions } from '../../hooks/usePermissions';
import { InvoiceToAmendRows } from './InvoicesToAmendDialog';

interface AlreadyInvoicedDialogProps {
  /** Empty = nothing to ask: the dialog renders nothing. */
  entries: AlreadyInvoicedEnrolment[];
  onClose: () => void;
}

/** The Create Invoice deep link, prefilled for one enrolment (InvoicesPage reads and clears it). */
export function createInvoiceLinkFor(schoolSlug: string | undefined, entry: AlreadyInvoicedEnrolment): string {
  const params = new URLSearchParams({
    create: '1',
    familyId: entry.familyId,
    termId: entry.termId,
    studentId: entry.studentId,
    enrollmentId: entry.enrollmentId,
  });
  return `/${schoolSlug}/admin/invoices?${params.toString()}`;
}

/** The invoice page's edit / amend deep link, with no class banner (nothing about the class changed). */
export function invoiceLinkFor(schoolSlug: string | undefined, invoiceId: string): string {
  return `/${schoolSlug}/admin/invoices/${invoiceId}?amend=1`;
}

const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;

/** "Term 3 2026", "Term 3 2026 and Term 4 2026", "A, B and C". */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

interface FamilyGroup {
  familyId: string;
  familyName: string;
  invoices: InvoiceToAmend[];
  entries: AlreadyInvoicedEnrolment[];
}

interface TermGroup {
  termId: string;
  termName: string;
  families: FamilyGroup[];
}

/** Group by term (in arrival order), then by family; a family's invoices are de-duplicated. */
export function groupAlreadyInvoiced(entries: AlreadyInvoicedEnrolment[]): TermGroup[] {
  const terms: TermGroup[] = [];
  for (const entry of entries) {
    let term = terms.find((t) => t.termId === entry.termId);
    if (!term) {
      term = { termId: entry.termId, termName: entry.termName, families: [] };
      terms.push(term);
    }
    let family = term.families.find((f) => f.familyId === entry.familyId);
    if (!family) {
      family = { familyId: entry.familyId, familyName: entry.familyName, invoices: [], entries: [] };
      term.families.push(family);
    }
    for (const invoice of entry.invoices) {
      if (!family.invoices.some((i) => i.id === invoice.id)) family.invoices.push(invoice);
    }
    family.entries.push(entry);
  }
  return terms;
}

export default function AlreadyInvoicedDialog({ entries, onClose }: AlreadyInvoicedDialogProps) {
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  const { can } = usePermissions();
  if (entries.length === 0) return null;

  const canCreate = can('operations_invoices', 'write');
  const groups = groupAlreadyInvoiced(entries);
  const multiTerm = groups.length > 1;
  const termNames = joinNames(groups.map((g) => g.termName));
  const studentNames = [...new Set(entries.map((e) => e.studentName))];
  const familyCount = new Set(entries.map((e) => e.familyId)).size;
  // One CHILD, by id: two children who share a name are still two (and their families show).
  const single = new Set(entries.map((e) => e.studentId)).size === 1;

  const title = single
    ? `${studentNames[0]}'s family was already invoiced for ${termNames}`
    : familyCount === 1
      ? `This family was already invoiced for ${termNames}`
      : `These families were already invoiced for ${termNames}`;
  const who = single ? firstName(studentNames[0]) : 'these students';
  const whose = familyCount === 1 ? "the family's" : "the families'";
  const body =
    (single
      ? `${who}'s new class isn't on ${whose} ${termNames} invoice. The next term-invoice run normally bills it on a separate invoice. `
      : `These classes aren't on ${whose} ${termNames} invoices. The next term-invoice run normally bills them on separate invoices. `) +
    `To bill ${single ? 'it' : 'them'} now instead, create an invoice, or add ${single ? 'it' : 'them'} to an unpaid invoice below ` +
    '(a draft picks the weeks; amending a sent one takes a line typed by hand).';
  const footnote = single
    ? `${firstName(studentNames[0])} may already be on one of these invoices if it was made by hand — check before billing.`
    : 'A student may already be on one of these invoices if it was made by hand — check before billing.';

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="already-invoiced-title">
      <DialogTitle id="already-invoiced-title">{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 1 }}>
          {body}
        </Typography>
        {groups.map((term) => (
          <Box key={term.termId} sx={{ mt: 1 }} data-testid={`already-invoiced-term-${term.termId}`}>
            {multiTerm && (
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                {term.termName}
              </Typography>
            )}
            {term.families.map((family) => (
              <Box
                key={family.familyId}
                sx={{ mt: 1 }}
                data-testid={`already-invoiced-family-${term.termId}-${family.familyId}`}
              >
                {!single && (
                  <Typography variant="body2" fontWeight={700}>
                    {family.familyName}
                  </Typography>
                )}
                {/* The family's invoices FIRST: the child may belong on one of them. */}
                <InvoiceToAmendRows
                  invoices={family.invoices}
                  linkFor={(invoiceId) => invoiceLinkFor(schoolSlug, invoiceId)}
                />
                {canCreate && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                    {family.entries.map((entry) => (
                      <Button
                        key={entry.enrollmentId}
                        size="small"
                        variant="text"
                        component={RouterLink}
                        to={createInvoiceLinkFor(schoolSlug, entry)}
                      >
                        Create an invoice for {firstName(entry.studentName)}
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        ))}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {footnote} After each one, your browser&apos;s Back button returns you to this list.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
